/*
  Enquire modal — B2B mode.
  Listens for clicks on any [data-enquire-trigger], fills in product context,
  and submits the enquiry to Shopify's /contact endpoint (reliable email
  delivery) with a best-effort Slack notification.

  Delivery model:
  - The form submits NATIVELY to /contact (action="/contact"). This is the
    same path the wholesale application uses and is the reliable way to make
    Shopify send the contact-form notification email. Shopify then reloads the
    page with ?contact_posted=true.
  - Slack is fired via navigator.sendBeacon BEFORE navigation so it is not lost.
  - After the reload we surface a confirmation toast to the buyer.

  Previously this used fetch() + Promise.allSettled, which (a) never triggered a
  native submission and (b) masked failures behind a fake success message, so
  enquiries silently failed to deliver.
*/
(function () {
  "use strict";

  var SUBMIT_FLAG = "vianneEnquirySubmitted";

  function init() {
    const modal = document.querySelector("[data-enquire-modal]");

    // Show the post-submit confirmation toast even if the modal markup is not
    // on this particular page (it normally is, since it's in the layout).
    maybeShowConfirmation();

    if (!modal) return;

    const form = modal.querySelector("[data-enquire-form]");
    const submitBtn = modal.querySelector("[data-enquire-submit]");
    const slackWebhook = modal.dataset.slackWebhook || "";

    const productTitleEl = modal.querySelector("[data-enquire-product-title]");
    const productSkuEl = modal.querySelector("[data-enquire-product-sku]");
    const productImageEl = modal.querySelector("[data-enquire-product-image]");

    const productTitleInput = modal.querySelector("[data-enquire-product-title-input]");
    const productUrlInput = modal.querySelector("[data-enquire-product-url-input]");
    const productSkuInput = modal.querySelector("[data-enquire-product-sku-input]");
    const productIdInput = modal.querySelector("[data-enquire-product-id-input]");

    function openModal(trigger) {
      const title = trigger.dataset.productTitle || "";
      const url = trigger.dataset.productUrl || "";
      const sku = trigger.dataset.productSku || "";
      const id = trigger.dataset.productId || "";
      const image = trigger.dataset.productImage || "";

      if (productTitleEl) productTitleEl.textContent = title || "—";
      if (productSkuEl) productSkuEl.textContent = sku || "—";
      if (productImageEl) {
        if (image) {
          productImageEl.src = image;
          productImageEl.style.display = "";
        } else {
          productImageEl.style.display = "none";
        }
      }
      if (productTitleInput) productTitleInput.value = title;
      if (productUrlInput) productUrlInput.value = url;
      if (productSkuInput) productSkuInput.value = sku;
      if (productIdInput) productIdInput.value = id;

      if (typeof modal.showModal === "function") {
        modal.showModal();
      } else {
        modal.setAttribute("open", "");
        modal.style.display = "block";
      }

      const firstInput = form.querySelector('input[name="contact[name]"]');
      if (firstInput) firstInput.focus();
    }

    function closeModal() {
      if (typeof modal.close === "function") modal.close();
      modal.removeAttribute("open");
      modal.style.display = "";
    }

    function buildSlackPayload(data) {
      const productTitle = data["contact[Product]"] || "(no product)";
      const productUrl = data["contact[Product URL]"] || "";
      const sku = data["contact[SKU]"] || "—";
      const name = data["contact[name]"] || "—";
      const company = data["contact[Company]"] || "—";
      const email = data["contact[email]"] || "—";
      const phone = data["contact[Phone]"] || "—";
      const qty = data["contact[Quantity]"] || "—";
      const message = data["contact[body]"] || "—";

      return {
        text: "New product enquiry: " + productTitle + " — from " + name + " at " + company,
        blocks: [
          {
            type: "header",
            text: { type: "plain_text", text: "🔔 New Product Enquiry" },
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: "*Product:*\n" + productTitle },
              { type: "mrkdwn", text: "*SKU:*\n" + sku },
              { type: "mrkdwn", text: "*Name:*\n" + name },
              { type: "mrkdwn", text: "*Company:*\n" + company },
              { type: "mrkdwn", text: "*Email:*\n" + email },
              { type: "mrkdwn", text: "*Phone:*\n" + phone },
              { type: "mrkdwn", text: "*Quantity:*\n" + qty },
            ],
          },
          {
            type: "section",
            text: { type: "mrkdwn", text: "*Message:*\n" + message },
          },
          productUrl && {
            type: "section",
            text: { type: "mrkdwn", text: "<" + productUrl + "|View product →>" },
          },
        ].filter(Boolean),
      };
    }

    function notifySlack(data) {
      if (!slackWebhook) return;
      try {
        const payload = JSON.stringify(buildSlackPayload(data));
        if (navigator.sendBeacon) {
          // Blob keeps the body intact across the imminent navigation.
          navigator.sendBeacon(slackWebhook, new Blob([payload], { type: "text/plain;charset=UTF-8" }));
        } else {
          // keepalive lets the request outlive the page unload.
          fetch(slackWebhook, { method: "POST", body: payload, keepalive: true }).catch(function () {});
        }
      } catch (err) {
        console.warn("[enquire] Slack notify failed (non-fatal)", err);
      }
    }

    function handleSubmit() {
      // IMPORTANT: do NOT preventDefault. Allow the native POST to /contact so
      // Shopify reliably sends the contact-form notification email. Fire Slack
      // first so it survives the navigation, and flag the submit so we can show
      // a confirmation toast after the reload.
      try {
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        notifySlack(data);
        try {
          sessionStorage.setItem(SUBMIT_FLAG, "1");
        } catch (e) {}
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = "Sending…";
        }
      } catch (err) {
        // Never block the native submission if the pre-submit hook fails.
        console.warn("[enquire] pre-submit hook failed (non-fatal)", err);
      }
    }

    document.addEventListener("click", function (event) {
      const trigger = event.target.closest("[data-enquire-trigger]");
      if (trigger) {
        event.preventDefault();
        openModal(trigger);
        return;
      }
      const closer = event.target.closest("[data-enquire-close]");
      if (closer) {
        event.preventDefault();
        closeModal();
      }
    });

    modal.addEventListener("click", function (event) {
      if (event.target === modal) closeModal();
    });

    form.addEventListener("submit", handleSubmit);
  }

  function maybeShowConfirmation() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("contact_posted") !== "true") return;
      if (sessionStorage.getItem(SUBMIT_FLAG) !== "1") return;
      sessionStorage.removeItem(SUBMIT_FLAG);
      showToast("Thanks — we received your enquiry. Our team will be in touch shortly.");
    } catch (e) {}
  }

  function showToast(message) {
    const toast = document.createElement("div");
    toast.setAttribute("role", "status");
    toast.textContent = message;
    toast.style.cssText = [
      "position:fixed",
      "left:50%",
      "bottom:24px",
      "transform:translateX(-50%)",
      "z-index:2147483000",
      "max-width:min(92vw,420px)",
      "background:#123016",
      "color:#f4dfb2",
      "padding:14px 18px",
      "border:1px solid rgba(244,223,178,.35)",
      "border-radius:8px",
      "font-size:14px",
      "line-height:1.4",
      "box-shadow:0 12px 30px rgba(0,0,0,.25)",
    ].join(";");
    document.body.appendChild(toast);
    setTimeout(function () {
      toast.style.transition = "opacity .4s ease";
      toast.style.opacity = "0";
      setTimeout(function () {
        toast.remove();
      }, 450);
    }, 5000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
